import { LocationType } from '~/client/graph/types.generated'
import MapViewLocationIcon, {
  LIBRARY_ICONS,
} from '~/client/src/shared/enums/SitemapAttributeIcon'
import {
  type PictogramIconName,
  PICTOGRAMS,
  pictogramSlugOf,
  type PictogramSlug,
  toPictogramIconName,
} from '~/client/src/shared/icons/pictograms/pictogramCatalog'

import { INFORMATION_POINT_KINDS } from './informationPointGroups'

/**
 * The one table translating between the legacy {@link LocationType} kinds and the
 * `data.type` slugs that replace them (STX1-597).
 *
 * Two discriminators are being separated. {@link LocationType} survives as the RESOURCE
 * type — is this row a location, a sensor, a material, an equipment — while the location
 * KIND (zone, gate, building) becomes a slug in `data.type`. Nearly every silent
 * regression this collapse can cause comes from one field having done both jobs.
 *
 * This mirrors `StruxHub.Graph.Entities/Resources/LocationKind.cs` and
 * `LocationTypeHierarchy.cs`. The two copies exist because the frontend has to read the
 * kind before the backend has finished publishing it, and `locationKind.test.ts` is what
 * makes two copies acceptable: it pins the slug set so drift fails a test rather than
 * showing a blank type with no error anywhere.
 */

/** The stable `data` key holding a location's kind. */
export const LOCATION_KIND_PROPERTY = 'type'

/**
 * The 12 root kinds — every kind a `LocationType` member used to encode.
 *
 * Two spellings a mechanical PascalCase→snake_case transform gets wrong, and both are
 * deliberate:
 *
 * - `LogisticsObject` → `information_points`. The hierarchy names this one for its label;
 *   `logistics_object` matches no option.
 * - `ProjectLocation` → `project_location`, not `project`. Every persisted vocabulary
 *   already spells it `ProjectLocation`; only the C# enum member, which the collapse
 *   deletes, said `Project`.
 */
export const LocationKind = {
  ProjectLocation: 'project_location',
  Building: 'building',
  Zone: 'zone',
  Staging: 'staging',
  Level: 'level',
  Area: 'area',
  Route: 'route',
  Gate: 'gate',
  InteriorPath: 'interior_path',
  InteriorDoor: 'interior_door',
  VerticalObject: 'vertical_object',
  InformationPoints: 'information_points',
} as const

export type LocationKindSlug = (typeof LocationKind)[keyof typeof LocationKind]

/** A kind slug, root or leaf. Leaves are not in {@link LocationKind}. */
export type LocationKindValue = string

/**
 * The hierarchy, in display order: 12 roots, each followed by its leaves. 47 options,
 * mirroring `LocationTypeHierarchy.BuildOptions()`.
 *
 * A project gets its own copy of this list when it is first seeded and owns it from then
 * on, so an existing project matches this table in membership but not necessarily in
 * display order — read a project's own options when rendering a picker, and use this
 * table only for the root/leaf relation, which is structural.
 */
const LEAVES_BY_ROOT: Readonly<Record<string, readonly string[]>> = {
  [LocationKind.VerticalObject]: [
    'general_vertical_object',
    'stairway',
    'elevator',
    'shaft',
  ],
  [LocationKind.InformationPoints]: INFORMATION_POINT_KINDS,
}

/** Every root slug, in hierarchy display order. */
export const LOCATION_KIND_ROOTS: readonly LocationKindSlug[] =
  Object.values(LocationKind)

/** Every slug in the hierarchy, roots and leaves, in display order. */
export const LOCATION_KIND_SLUGS: readonly string[] =
  LOCATION_KIND_ROOTS.flatMap(root => [root, ...(LEAVES_BY_ROOT[root] ?? [])])

const ROOT_BY_SLUG: Readonly<Record<string, string>> = Object.fromEntries(
  LOCATION_KIND_ROOTS.flatMap(root => [
    [root, root],
    ...(LEAVES_BY_ROOT[root] ?? []).map(leaf => [leaf, root]),
  ]),
)

const SLUG_BY_LOCATION_TYPE: Readonly<
  Partial<Record<LocationType, LocationKindSlug>>
> = {
  [LocationType.ProjectLocation]: LocationKind.ProjectLocation,
  [LocationType.Building]: LocationKind.Building,
  [LocationType.Zone]: LocationKind.Zone,
  [LocationType.Staging]: LocationKind.Staging,
  [LocationType.Level]: LocationKind.Level,
  [LocationType.Area]: LocationKind.Area,
  [LocationType.Route]: LocationKind.Route,
  [LocationType.Gate]: LocationKind.Gate,
  [LocationType.InteriorPath]: LocationKind.InteriorPath,
  [LocationType.InteriorDoor]: LocationKind.InteriorDoor,
  [LocationType.VerticalObject]: LocationKind.VerticalObject,
  [LocationType.LogisticsObject]: LocationKind.InformationPoints,
}

/**
 * The kind slug for a legacy {@link LocationType}, or `null` when the type is not a
 * location kind at all.
 *
 * `null` for `OffloadingEquipment`, `Sensor`, `Material` and `Integration`: those are
 * resource types, not kinds of place, and none of them appears in the hierarchy. It is
 * also `null` for the collapsed `Location` type once the backend adds it, because a plain
 * location's kind lives in its data — use {@link getLocationKind} to read it.
 */
export function kindFromLocationType(
  type: LocationType | null | undefined,
): LocationKindSlug | null {
  return (type && SLUG_BY_LOCATION_TYPE[type]) || null
}

/**
 * The root kind for a slug: a leaf resolves to its parent, a root resolves to itself.
 *
 * The hierarchy is two levels deep — `elevator`, `stairway` and `shaft` sit under
 * `vertical_object`, and thirty-one leaves under `information_points`. A migrated location
 * can legitimately be classified as a leaf, so comparing its kind directly against a root
 * is false for exactly the locations that are of that kind. Anything asking "is this a
 * vertical object?" must compare roots — which is why {@link isLocationOfKind} does it
 * for you.
 *
 * An unrecognised slug passes through unchanged rather than becoming `null`, matching
 * `LocationKind.RootOf`. A project that has added its own option is classified, not
 * unknown, and blanking it here would hide it from every kind-keyed view.
 */
export function rootOfKind(
  slug: LocationKindValue | null | undefined,
): string | null {
  if (!slug) return null

  return ROOT_BY_SLUG[slug] ?? slug
}

/**
 * A kind together with everything classified under it: a root yields itself plus its
 * leaves, a leaf yields just itself.
 *
 * What "locations of kind X" has to mean when filtering. A location classified as
 * `elevator` is a vertical object, so asking for `vertical_object` and matching the
 * stored value exactly would miss precisely the locations that are of that kind.
 */
export function kindWithDescendants(
  slug: LocationKindValue | null | undefined,
): readonly string[] {
  if (!slug) return []

  return [slug, ...(LEAVES_BY_ROOT[slug] ?? [])]
}

/**
 * Stored `iconName` → the hierarchy leaf it names.
 *
 * Mirrors `CollapseLocationTypeToResourceMigration.LeafByIconName`, and for the same
 * reason: the map palette is what creates these locations, and each of its icons pairs
 * with exactly one leaf. The migration reads this table to classify existing rows; the
 * creation flow reads it to classify new ones, so a location drawn as an elevator today
 * and one migrated from an elevator icon land on the same slug.
 *
 * Only leaves appear here. The ten kinds with no sub-kinds take their root from the
 * object being drawn, not from its icon.
 */
/**
 * The selectable leaf under each root that HAS children.
 *
 * Those two roots are navigational: the hierarchy picker drills into a parent rather than
 * offering it, and the record validator refuses a non-leaf. So a surface that means "just a
 * vertical object" stores this instead of the root. Mirrors
 * `CollapseLocationTypeToResourceMigration.GeneralLeafByRoot`.
 */
export const GENERAL_LEAF_BY_ROOT: Readonly<Record<string, string>> = {
  [LocationKind.VerticalObject]: 'general_vertical_object',
  [LocationKind.InformationPoints]: 'general_information_point',
}

const LEAF_BY_ICON_NAME: Readonly<Record<string, string>> = {
  // The Information Points root's own glyph, which belongs to no leaf, names the General
  // leaf beneath it. The Vertical Object root has no glyph of its own - it shares `stairs`
  // with its Stairway child - so its General leaf is carried explicitly by the palette
  // entry instead, and appears only in the icon-BY-leaf direction below.
  logistics: 'general_information_point',
  stairs: 'stairway',
  elevator: 'elevator',
  shaft: 'shaft',
  bathroom: 'restrooms',
  break: 'break_area',
  canteen: 'canteen',
  dumpster: 'waste',
  entrance: 'access',
  'hand-wash': 'handwash_station',
  medical: 'medical_area',
  'meeting-point': 'muster_point',
  parking: 'parking',
  smoking: 'smoking_area',
  temperature: 'medical_area',
  tent: 'break_area',
  toilet: 'restrooms',
  walkway: 'walkway',
  'electrical-room': 'electrical_room',
  trailer: 'trailer',
}

/**
 * The legacy {@link LocationType} a kind slug corresponds to - the inverse of
 * {@link kindFromLocationType}, resolving a leaf to its root first.
 *
 * Needed wherever a slug has to be handed back to code that still speaks the enum. The
 * client is one such place for as long as the kind stores rehydrate rows into per-kind
 * model classes: those hardcode a `LocationType`, so a filter compared against a model
 * has to be expressed in the enum, whatever vocabulary it was authored in.
 *
 * Leaves resolve through their root because the enum has no member for them - a location
 * classified `elevator` is a `VerticalObject` to anything asking in enum terms.
 */
export function locationTypeFromKind(
  slug: LocationKindValue | null | undefined,
): LocationType | null {
  const root = rootOfKind(slug)

  return (root && LOCATION_TYPE_BY_ROOT[root]) || null
}

const LOCATION_TYPE_BY_ROOT: Readonly<Record<string, LocationType>> =
  Object.fromEntries(
    Object.entries(SLUG_BY_LOCATION_TYPE).map(([type, kindSlug]) => [
      kindSlug,
      type as LocationType,
    ]),
  )

/**
 * The kind an icon names, for a location being created as `root`.
 *
 * The leaf only wins when it actually sits under that root, so a stray icon cannot
 * classify a zone as an elevator — the same guard the migration applies to stored rows.
 * Falls back to the root, which is the right answer for the ten kinds that have no
 * sub-kinds and for an icon the table does not know.
 *
 * Matched case-insensitively: the casing of stored icon names is not guaranteed.
 */
export function kindFromIconName(
  iconName: string | null | undefined,
  root: LocationKindValue | null | undefined,
): string | null {
  if (!root) return null

  const leaf = iconName ? LEAF_BY_ICON_NAME[iconName.toLowerCase()] : undefined

  return leaf && rootOfKind(leaf) === rootOfKind(root) ? leaf : root
}

/**
 * Root kind → the glyph the map palette draws it with.
 *
 * The same pairing `LOCATION_ICON_BY_TYPE` holds, keyed by the slug rather than the
 * {@link LocationType} member the collapse removes - except Information Points, drawn with
 * their library icon.
 */
const ICON_NAME_BY_ROOT: Readonly<Record<string, MapViewLocationIcon>> = {
  [LocationKind.ProjectLocation]: MapViewLocationIcon.Project,
  [LocationKind.Building]: MapViewLocationIcon.Building,
  [LocationKind.Zone]: MapViewLocationIcon.Zone,
  [LocationKind.Staging]: MapViewLocationIcon.Staging,
  [LocationKind.Level]: MapViewLocationIcon.Level,
  [LocationKind.Area]: MapViewLocationIcon.Area,
  [LocationKind.Route]: MapViewLocationIcon.Route,
  [LocationKind.Gate]: MapViewLocationIcon.Gate,
  [LocationKind.InteriorPath]: MapViewLocationIcon.InteriorPath,
  [LocationKind.InteriorDoor]: MapViewLocationIcon.InteriorDoor,
  [LocationKind.VerticalObject]: MapViewLocationIcon.Stairs,
}

/**
 * StruxHub glyphs drawn for the Information Point and location types on the Figma board → the
 * kinds they belong to, home first. Only what the picker lists for a kind; drawing a kind with a
 * glyph by default is ownIconName's job.
 */
const GLYPH_KINDS: Partial<Record<MapViewLocationIcon, readonly string[]>> = {
  [MapViewLocationIcon.Stairs]: ['stairway', 'elevated_access'],
  [MapViewLocationIcon.Truck]: ['zone', 'traffic_control'],
  [MapViewLocationIcon.ConcreteMixerTruck]: ['zone'],
  [MapViewLocationIcon.EquipmentStorage]: ['staging'],
  [MapViewLocationIcon.MaterialStorageBoxes]: ['staging'],
  [MapViewLocationIcon.MaterialStorageWood]: ['staging'],
  [MapViewLocationIcon.MaterialStorageSteel]: ['staging'],
  [MapViewLocationIcon.MaterialStoragePipes]: ['staging'],
  [MapViewLocationIcon.MaterialLiftingSteel]: ['staging'],
  [MapViewLocationIcon.Scaffolding]: ['elevated_access'],
  [MapViewLocationIcon.SwingStage]: ['elevated_access'],
  [MapViewLocationIcon.PersonnelLift]: ['elevated_access'],
  [MapViewLocationIcon.Info]: ['general_information_point'],
  [MapViewLocationIcon.SiteMap]: ['general_information_point'],
  [MapViewLocationIcon.LogOut]: ['exit'],
  [MapViewLocationIcon.NoEntry]: ['restricted_area', 'traffic_control'],
  [MapViewLocationIcon.Stop]: ['restricted_area', 'traffic_control'],
  [MapViewLocationIcon.NoPedestrians]: ['restricted_area'],
  [MapViewLocationIcon.NoAccess]: ['restricted_area'],
  [MapViewLocationIcon.SecurityCheckpointWindow]: ['security_checkpoint'],
  [MapViewLocationIcon.VisitorCheck]: ['security_checkpoint', 'site_office'],
  [MapViewLocationIcon.HighVisibilityVest]: ['ppe_zone'],
  [MapViewLocationIcon.SafetyHelmet]: ['ppe_zone'],
  [MapViewLocationIcon.SafetyGloves]: ['ppe_zone'],
  [MapViewLocationIcon.SafetyToeBoots]: ['ppe_zone'],
  [MapViewLocationIcon.PpeNotRequired]: ['ppe_zone'],
  [MapViewLocationIcon.NoVest]: ['ppe_zone'],
  [MapViewLocationIcon.NoHelmet]: ['ppe_zone'],
  [MapViewLocationIcon.NoGloves]: ['ppe_zone'],
  [MapViewLocationIcon.NoBoots]: ['ppe_zone'],
  [MapViewLocationIcon.CleanroomGowning]: ['controlled_environment'],
  [MapViewLocationIcon.IcraZone]: ['controlled_environment'],
  [MapViewLocationIcon.NegativePressure]: ['controlled_environment'],
  [MapViewLocationIcon.EsdZone]: ['controlled_environment'],
  [MapViewLocationIcon.NoPhotography]: ['site_rules'],
  [MapViewLocationIcon.NoDrones]: ['site_rules'],
  [MapViewLocationIcon.FallHazard]: ['hazard_zone'],
  [MapViewLocationIcon.SilicaDust]: ['hazard_zone'],
  [MapViewLocationIcon.Noise]: ['hazard_zone'],
  [MapViewLocationIcon.ConfinedSpace]: ['confined_space'],
  [MapViewLocationIcon.EnergizedEquipment]: ['energized_equipment'],
  [MapViewLocationIcon.BikeParking]: ['parking'],
  [MapViewLocationIcon.UtilityCart]: ['parking'],
  [MapViewLocationIcon.LoadingZone]: ['loading'],
  [MapViewLocationIcon.FireLane]: ['traffic_control'],
  [MapViewLocationIcon.FlaggerAhead]: ['traffic_control'],
  [MapViewLocationIcon.OneWay]: ['traffic_control'],
  [MapViewLocationIcon.SpeedLimit]: ['traffic_control'],
  [MapViewLocationIcon.Trash]: ['waste'],
  [MapViewLocationIcon.AdminOffice]: ['site_office'],
  [MapViewLocationIcon.HandWashStation]: ['utilities'],
  [MapViewLocationIcon.SpillKit]: ['stormwater_erosion_control'],
  [MapViewLocationIcon.ErosionControl]: ['stormwater_erosion_control'],
  [MapViewLocationIcon.StormDrainInletProtection]: [
    'stormwater_erosion_control',
  ],
  [MapViewLocationIcon.ConcreteWashout]: ['stormwater_erosion_control'],
  [MapViewLocationIcon.WheelWash]: ['stormwater_erosion_control'],
  [MapViewLocationIcon.RestroomMen]: ['restrooms'],
  [MapViewLocationIcon.RestroomWomen]: ['restrooms'],
  [MapViewLocationIcon.Sofa]: ['break_area'],
  [MapViewLocationIcon.Hydration]: ['hydration_station'],
  [MapViewLocationIcon.HeartPulse]: ['medical_area'],
  [MapViewLocationIcon.Eyewash]: ['medical_area'],
  [MapViewLocationIcon.FireExtinguisher]: ['fire_protection'],
  [MapViewLocationIcon.FireHydrant]: ['fire_protection'],
  [MapViewLocationIcon.FireAlarmButton]: ['fire_protection'],
  [MapViewLocationIcon.StandpipeHose]: ['fire_protection'],
  [MapViewLocationIcon.Flame]: ['hazard_zone'],
  [MapViewLocationIcon.Excavator]: ['hazard_zone'],
  [MapViewLocationIcon.OverheadLoad]: ['hazard_zone'],
}

const GLYPHS_BY_KIND: Readonly<Record<string, readonly string[]>> = (() => {
  const own: Record<string, string[]> = {}
  const shared: Record<string, string[]> = {}
  Object.entries(GLYPH_KINDS).forEach(([glyph, kinds]) =>
    kinds.forEach((kind, index) =>
      ((index === 0 ? own : shared)[kind] ??= []).push(glyph),
    ),
  )
  return Object.fromEntries(
    [...new Set([...Object.keys(own), ...Object.keys(shared)])].map(kind => [
      kind,
      [...(own[kind] ?? []), ...(shared[kind] ?? [])],
    ]),
  )
})()

/**
 * Kind → the library icons that belong to it. An icon's first kind is its home: a kind lists its
 * own icons first, in catalog order, then the ones it shares from other kinds.
 */
const PICTOGRAMS_BY_KIND: Readonly<Record<string, readonly PictogramSlug[]>> =
  (() => {
    const own: Record<string, PictogramSlug[]> = {}
    const shared: Record<string, PictogramSlug[]> = {}
    PICTOGRAMS.forEach(({ slug, kinds }) =>
      kinds.forEach((kind, index) =>
        ((index === 0 ? own : shared)[kind] ??= []).push(slug),
      ),
    )
    return Object.fromEntries(
      [...new Set([...Object.keys(own), ...Object.keys(shared)])].map(kind => [
        kind,
        [...(own[kind] ?? []), ...(shared[kind] ?? [])],
      ]),
    )
  })()

/** The library icons that belong to a kind, its default first. */
export function pictogramsOfKind(
  slug: LocationKindValue | null | undefined,
): readonly PictogramSlug[] {
  return (slug && PICTOGRAMS_BY_KIND[slug]) || []
}

/**
 * Information Points are drawn with the first library icon of their kind, not the legacy glyph
 * their kind was named after. The root is drawn as its General leaf. Every other kind keeps its
 * StruxHub glyph; its library icons are only offered first in the picker.
 */
const defaultPictogramOf = (slug: string): PictogramSlug | undefined =>
  rootOfKind(slug) === LocationKind.InformationPoints
    ? pictogramsOfKind(GENERAL_LEAF_BY_ROOT[slug] ?? slug)[0]
    : undefined

const DEFAULT_PICTOGRAMS: ReadonlySet<PictogramSlug> = new Set(
  INFORMATION_POINT_KINDS.map(kind => pictogramsOfKind(kind)[0]),
)

// Information Points are drawn with library icons, and several legacy glyphs name some of their
// leaves, so only the other leaves are inverted.
const ICON_NAME_BY_LEAF: Readonly<Record<string, MapViewLocationIcon>> = {
  ...(Object.fromEntries(
    Object.entries(LEAF_BY_ICON_NAME)
      .filter(([, leaf]) => rootOfKind(leaf) !== LocationKind.InformationPoints)
      .map(([iconName, leaf]) => [leaf, iconName as MapViewLocationIcon]),
  ) as Record<string, MapViewLocationIcon>),
  // Not inverted from the table above: `stairs` there names `stairway`, and one glyph cannot
  // name two leaves. A generic vertical object is drawn with the family's glyph all the same -
  // what distinguishes it from a stairway is its kind, which the palette carries explicitly.
  general_vertical_object: MapViewLocationIcon.Stairs,
}

const ownIconName = (
  slug: string,
): MapViewLocationIcon | PictogramIconName | undefined => {
  const sign = defaultPictogramOf(slug)

  return sign
    ? toPictogramIconName(sign)
    : (ICON_NAME_BY_LEAF[slug] ?? ICON_NAME_BY_ROOT[slug])
}

/**
 * The icon a kind is drawn with, falling back to the root's for a leaf the tables do not know
 * (a project's own added option). For StruxHub glyphs this is the inverse of
 * {@link kindFromIconName}; an Information Point is drawn with its library sign instead, which
 * classifies nothing.
 *
 * Deliberately one-way. Picking a type picks the icon, because a type with no artwork of
 * its own would otherwise keep whatever glyph it was drawn with; picking an icon leaves
 * the type alone, because the icon is how a location is drawn and the kind is what it is —
 * re-deriving one from the other would reclassify a record every time it was restyled.
 */
export function iconNameFromKind(
  slug: LocationKindValue | null | undefined,
): MapViewLocationIcon | PictogramIconName | null {
  if (!slug) return null

  const root = rootOfKind(slug)

  return ownIconName(slug) ?? (root ? ownIconName(root) : null) ?? null
}

const KIND_BY_ROOT_GLYPH: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(ICON_NAME_BY_ROOT).map(([kind, iconName]) => [iconName, kind]),
)

/**
 * Every kind an icon belongs to, its home first: a library icon's kinds, a StruxHub glyph's
 * listed kinds, else the leaf or root it names. Empty for an icon of no kind (a company logo).
 */
export function kindsOfIcon(iconName: string): readonly string[] {
  const slug = pictogramSlugOf(iconName)
  if (slug) return PICTOGRAMS.find(p => p.slug === slug)?.kinds ?? []

  const named = LEAF_BY_ICON_NAME[iconName] ?? KIND_BY_ROOT_GLYPH[iconName]
  return GLYPH_KINDS[iconName as MapViewLocationIcon] ?? (named ? [named] : [])
}

/** The kind an icon belongs to first, its home. Null for an icon of no kind. */
export function homeKindOfIcon(iconName: string): string | null {
  return kindsOfIcon(iconName)[0] ?? null
}

/**
 * Every icon that belongs to a kind, the one it is drawn with first: its library icons, then the
 * StruxHub glyphs that name it. A root with children answers for its General leaf.
 */
export function iconNamesOfKind(
  slug: LocationKindValue | null | undefined,
): readonly string[] {
  if (!slug) return []

  const kind = GENERAL_LEAF_BY_ROOT[slug] ?? slug
  const glyphs = Object.keys(LEAF_BY_ICON_NAME).filter(
    iconName => LEAF_BY_ICON_NAME[iconName] === kind,
  )

  return [
    ...new Set(
      [
        iconNameFromKind(kind),
        ...pictogramsOfKind(kind).map(toPictogramIconName),
        ...glyphs,
        ICON_NAME_BY_ROOT[kind],
        ...(GLYPHS_BY_KIND[kind] ?? []),
      ].filter(Boolean),
    ),
  ]
}

/**
 * Whether a library pictogram is the one a kind is drawn with, rather than a sign picked for
 * its own sake. Only the former follows a change of kind.
 */
function isKindPictogram(iconName: string | null | undefined): boolean {
  const slug = pictogramSlugOf(iconName)

  return !!slug && DEFAULT_PICTOGRAMS.has(slug)
}

/**
 * Whether a location's library icon (a sign or a library glyph) was picked for its own sake
 * rather than being one of its kind's own icons, the ones its Default tab offers. A picked
 * icon survives a change of kind; a kind's own icon follows it. Without the kind it was
 * changed from, only the sign a kind is drawn with by default counts as its own.
 */
export function isPickedLibraryIcon(
  iconName: string | null | undefined,
  previousKind?: string | null,
): boolean {
  if (!pictogramSlugOf(iconName) && !LIBRARY_ICONS.has(iconName)) {
    return false
  }

  return previousKind
    ? !iconNamesOfKind(previousKind).includes(iconName)
    : !isKindPictogram(iconName)
}

/** The shape {@link getLocationKind} needs — a DTO or a model, read-only. */
export interface IKindBearingLocation {
  readonly type?: LocationType | null
  readonly data?: unknown
}

/**
 * The kind explicitly stored on a record's `data`, or null.
 *
 * Exported for the map's creation card, which needs "is a kind actually stored here" WITHOUT
 * {@link getLocationKind}'s legacy fallback - that fallback resolves to the bare root, which
 * would demote every leaf the palette draws.
 */
export function readStoredKind(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null

  const stored = (data as Record<string, unknown>)[LOCATION_KIND_PROPERTY]

  return typeof stored === 'string' && stored.trim() ? stored : null
}

/**
 * The location's kind slug: what it was classified as in `data.type` if it has been
 * migrated, otherwise derived from its legacy {@link LocationType}.
 *
 * Dual-read on purpose. It is what lets every consumer move onto the slug while the enum
 * is still populated, so a switch can be proven behaviour-preserving before any data
 * changes — rather than the two happening at once with nothing to compare against.
 *
 * Returns `null` for a resource type that is not a kind of place, and for a collapsed
 * location whose `data.type` has not been written. Callers must decide what an
 * unclassified location means to them; silently bucketing it as one particular kind is
 * how a location vanishes from a view with no error.
 */
export function getLocationKind(
  location: IKindBearingLocation | null | undefined,
): string | null {
  if (!location) return null

  const legacyKind = kindFromLocationType(location.type)
  if (legacyKind) return readStoredKind(location.data) ?? legacyKind

  // `data.type` is NOT self-describing. It is the property name of whichever taxonomy the
  // row's resource type owns - LocationTypeHierarchy.PropertyName and
  // EquipmentTypeHierarchy.PropertyName are both "type" - so on an equipment row it holds
  // "boom_lift", on a material its material type, and reading it here without checking the
  // resource type first reports every resource in the collection as a location. Which is
  // the same field-doing-two-jobs mistake this whole change exists to undo, one level down.
  return location.type === LocationType.Location
    ? readStoredKind(location.data)
    : null
}

/**
 * Whether a row is a location at all — a place, rather than one of the sibling resource
 * types that share its collection.
 *
 * Deliberately separate from {@link getLocationKind}: "is this a location" and "what kind
 * of location is it" are different questions, and answering the first with the second
 * makes an unclassified location disappear instead of showing a blank kind. A location
 * with no kind is a location that needs one.
 */
export function isLocationResourceType(
  type: LocationType | null | undefined,
): boolean {
  return !!kindFromLocationType(type) || type === LocationType.Location
}

/**
 * The location's kind resolved to a hierarchy root — a location classified as `elevator`
 * reports `vertical_object`. This is the form that corresponds one-to-one with the legacy
 * {@link LocationType} kinds, so it is what a translated consumer compares against.
 */
export function getLocationRootKind(
  location: IKindBearingLocation | null | undefined,
): string | null {
  return rootOfKind(getLocationKind(location))
}

/**
 * Whether a location is of a kind, comparing at root level so a leaf matches its root.
 *
 * Prefer this over comparing {@link getLocationKind} directly: root resolution being the
 * default shape of the API is what stops a call site reproducing the leaf-vs-root bug the
 * backend already hit and fixed.
 */
export function isLocationOfKind(
  location: IKindBearingLocation | null | undefined,
  kind: LocationKindValue,
): boolean {
  const root = rootOfKind(kind)

  return !!root && getLocationRootKind(location) === root
}

/**
 * Whether a kind slug names the project's single site root.
 *
 * Slug-level rather than record-level because the surfaces that ask are form-layer: they
 * hold `recordData[LOCATION_KIND_PROPERTY]`, never the `{ type, data }` shape
 * {@link isLocationOfKind} reads - a resource card's record carries no legacy `type` at
 * all, so that predicate silently answers false for every card. Root-resolved, so the two
 * agree by construction.
 */
export function isProjectLocationKind(
  slug: LocationKindValue | null | undefined,
): boolean {
  return rootOfKind(slug) === LocationKind.ProjectLocation
}
